import React, { useEffect, useState } from 'react';
import { AuditLogEventName, StyledDivider } from 'pages/AuditLogPage/AuditLogViewPage/styles';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useNavigate } from 'react-router-dom';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { formatDate } from 'utils/date';
import { IAuditLogItemResponse } from 'models/auditLog';
import { Chips } from 'components/atoms/Chips';
import { findInDictionary } from 'helpers/dictionary';
import { licencePrintStatusVariant } from 'pages/LicencesPage/helpers';
import { useDictionary } from 'models/dictionaryContext';

export interface IPrintoutObject {
  id?: string;
  uniqueId?: string;
  licenceUniqueId?: string;
  licenceNumber?: string;
  licenceOwner?: string;
  licenceOwnerUniqueId?: string;
  licenceOwnerTypeUniqueId?: string;
  licenceAuthorityUniqueId?: string;
  statusUniqueId?: string;
  typeUniqueId?: string;
  batchUniqueId?: string;
  batchName?: string;
  createdDate?: string;
  printDate?: string;
  version?: number;
}

interface ILegalEntityLicencesPrintoutEventDetails {
  auditLog: IAuditLogItemResponse;
}

interface IRenderDetails {
  printoutDetail: IPrintoutObject;
  t: any;
}

const RenderDetails = ({ printoutDetail, t }: IRenderDetails) => {
  const { longDateFormat, getProperties } = useGlobalProperty();

  useEffect(() => {
    getProperties();
  }, []);

  const {
    weaponLicencePrintStatus,
    getWeaponLicencePrintStatus,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
  } = useDictionary();

  useEffect(() => {
    getWeaponLicencePrintStatus();
    getWeaponLicencingAuthority();
  }, []);

  return (
    <>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.printoutId.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(printoutDetail?.id)}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licenceNumber.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(printoutDetail?.licenceNumber)}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licenceOwnerName.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(printoutDetail?.licenceOwner)}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.status.label')}
        </Typography>
        <Typography variant="body2">
          {printoutDetail.statusUniqueId ? (
            <Chips
              text={findInDictionary(printoutDetail.statusUniqueId, weaponLicencePrintStatus)}
              fullWidth
              variant={licencePrintStatusVariant(printoutDetail.statusUniqueId)}
            />
          ) : (
            '-'
          )}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.licenceAuthority.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(
            findInDictionary(
              printoutDetail?.licenceAuthorityUniqueId,
              weaponLicencingAuthority,
            ),
          )}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.createDate.label')}
        </Typography>
        <Typography variant="body2">
          {printoutDetail?.createdDate
            ? formatDate(printoutDetail.createdDate, longDateFormat)
            : emptyFieldHolder('')}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.printDate.label')}
        </Typography>
        <Typography variant="body2">
          {printoutDetail?.printDate
            ? formatDate(printoutDetail.printDate, longDateFormat)
            : emptyFieldHolder('')}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('licences.batchName.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(printoutDetail?.batchName)}</Typography>
      </Grid>
    </>
  );
};

const LegalEntityLicencesPrintoutEventDetails = ({
  auditLog,
}: ILegalEntityLicencesPrintoutEventDetails) => {
  const [printoutDetails, setPrintoutDetails] = useState<IPrintoutObject | null>(null);
  const [previousPrintoutDetails, setPreviousPrintoutDetails] =
    useState<IPrintoutObject | null>(null);
  const { t } = useTranslations();
  const navigate = useNavigate();

  useEffect(() => {
    if (auditLog?.postObjectValue) {
      setPrintoutDetails(auditLog.postObjectValue as unknown as IPrintoutObject);
    } else {
      setPrintoutDetails(null);
    }

    if (auditLog?.previousObjectValue) {
      setPreviousPrintoutDetails(auditLog.previousObjectValue as unknown as IPrintoutObject);
    } else {
      setPreviousPrintoutDetails(null);
    }
  }, [auditLog]);

  const handleRedirect = (details?: IPrintoutObject | null) => {
    if (!details?.licenceOwnerUniqueId || !details?.licenceUniqueId) return;

    navigate(
      `${ERouteLinks.LegalEntityViewLicence.replace(
        ':id',
        details.licenceOwnerUniqueId,
      ).replace(':licenceId', details.licenceUniqueId)}`,
    );
  };

  return (
    <>
      {previousPrintoutDetails && (
        <>
          <AuditLogEventName>
            <Typography variant="h5">
              {`${t('module.printouts.name')} ${t('auditLogDetails.before.event.title')}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={previousPrintoutDetails?.licenceNumber || ''}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => handleRedirect(previousPrintoutDetails)}
          >
            <RenderDetails printoutDetail={previousPrintoutDetails} t={t} />
          </DetailsTemplate>
        </>
      )}

      {printoutDetails && (
        <>
          <AuditLogEventName sx={{ marginTop: previousPrintoutDetails ? '20px' : '0' }}>
            <Typography variant="h5">
              {`${t('module.printouts.name')} ${t(
                previousPrintoutDetails
                  ? 'auditLogDetails.after.event.title'
                  : 'auditLogDetails.post.event.title',
              )}`}
            </Typography>
          </AuditLogEventName>
          <DetailsTemplate
            title={printoutDetails?.licenceNumber || ''}
            pageType={EDetailsPageTypes.auditLogView}
            redirectToView={() => handleRedirect(printoutDetails)}
          >
            <RenderDetails printoutDetail={printoutDetails} t={t} />
          </DetailsTemplate>
        </>
      )}
    </>
  );
};

export default LegalEntityLicencesPrintoutEventDetails;
